import React from 'react';
import { UsageMeter } from '../../../ui/usage_meter/UsageMeter';
import { CandiBankStats } from '../../../utils/types/candi_bank_types';
import { creditTone, formatResetDate } from '../../../utils/types/credits_types';

interface BankStatsBarProps {
  stats: CandiBankStats | null;
  loading: boolean;
}

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; className?: string }> = ({
  label,
  value,
  sub,
  className = '',
}) => (
  <div className={`bg-white rounded-lg border border-slate-200 shadow-sm px-4 py-3 ${className}`}>
    <p className='text-[10px] font-bold text-slate-400 uppercase tracking-wider'>{label}</p>
    <p className='text-xl font-bold text-slate-900 tracking-tight tabular-nums leading-tight mt-0.5'>{value}</p>
    {sub && <p className='text-[11px] text-slate-500 font-medium mt-0.5'>{sub}</p>}
  </div>
);

const fmt = (n: number) => n.toLocaleString();

/** Headline numbers for the bank, plus this month's CV credit meter. */
export const BankStatsBar: React.FC<BankStatsBarProps> = ({ stats, loading }) => {
  if (loading && !stats) {
    return (
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 animate-pulse'>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-[74px] bg-slate-100 rounded-lg ${i === 3 ? 'col-span-2' : ''}`} />
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const { credits } = stats;
  const tone = creditTone(credits);
  const monthName = new Date(credits.resetsAt * 1000 - 1).toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  });

  const creditTileBg =
    tone === 'crit' ? 'bg-rose-50 border-rose-200' : tone === 'warn' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';

  return (
    <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-6' data-testid='bank-stats-bar'>
      <Tile label='Candidates' value={fmt(stats.totalCandidates)} sub='distinct people' />
      <Tile
        label='CVs in bank'
        value={fmt(stats.totalCvs)}
        sub={`${fmt(stats.bankUploads)} from uploads`}
      />
      <Tile
        label='Processing'
        value={fmt(stats.processing)}
        sub={stats.failed > 0 ? <span className='text-rose-600'>{fmt(stats.failed)} failed</span> : 'all parsed'}
      />
      <div className={`col-span-2 rounded-lg border shadow-sm px-4 py-3 ${creditTileBg}`}>
        <div className='flex items-center justify-between'>
          <p className='text-[10px] font-bold text-slate-500 uppercase tracking-wider'>
            CV credits · {monthName}
          </p>
          {tone === 'crit' && (
            <span className='text-[10px] font-bold text-rose-700 uppercase tracking-wider'>Limit reached</span>
          )}
        </div>
        {credits.unlimited ? (
          <>
            <p className='text-xl font-bold text-slate-900 tracking-tight leading-tight mt-0.5'>Unlimited</p>
            <p className='text-[11px] text-slate-500 font-medium mt-0.5'>
              {fmt(credits.used)} uploaded this month
            </p>
          </>
        ) : (
          <>
            <p className='text-xl font-bold text-slate-900 tracking-tight tabular-nums leading-tight mt-0.5'>
              {fmt(credits.remaining ?? 0)}
              <span className='text-[13px] font-semibold text-slate-500 ml-1.5'>
                of {fmt(credits.monthlyLimit ?? 0)} left
              </span>
            </p>
            <UsageMeter
              used={credits.used}
              limit={credits.monthlyLimit}
              tone={tone}
              className='mt-2'
              leftLabel={
                tone === 'crit'
                  ? 'Monthly limit reached — contact your admin'
                  : `${fmt(credits.used)} used`
              }
              rightLabel={`resets ${formatResetDate(credits)}`}
            />
          </>
        )}
      </div>
    </div>
  );
};
